import Close from "@/components/icons/Close";
import mediaGalleryStyles from "@/features/product/components/ProductMedia/media-gallery.module.css";
import useDevice from "@/hooks/useDevice";
import useLockBodyScroll from "@/hooks/useLockBodyScroll";
import useFocusTrap from "@pofo/focus-trap";
import * as Portal from "@radix-ui/react-portal";
import clsx from "clsx";
import { useTranslations } from "next-intl";
import Image, { ImageProps } from "next/image";
import { Suspense, useEffect, useRef } from "react";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import { Controller } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import Skeleton from "../Skeleton";
import Video from "../Video";
import styles from "./index.module.css";
import { ZoomProps } from "./ZoomImage";

const ZoomDialog = ({
  setSecondSwiper,
  firstSwiper,
  setCurrent,
  current,
  media,
  src,
  alt,
  fill,
  setOpen: setOpen,
  ...rest
}: ImageProps &
  ZoomProps & {
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  }) => {
  const imageRef = useRef<HTMLImageElement>(null!);
  const containerRef = useRef<HTMLElement>(null!);
  const { desktop } = useDevice();
  const { lockBodyScroll, unlockBodyScroll } = useLockBodyScroll();

  const currentMediaHref = media?.[current].href;

  const t = useTranslations();

  useFocusTrap(containerRef, true, {
    initialFocus: "first",
  });

  useEffect(() => {
    unlockBodyScroll();

    const onEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };

    // container.addEventListener("wheel", wheelZoom, { passive: false });
    document.addEventListener("keydown", onEscape);
    return () => {
      lockBodyScroll();
      // container.removeEventListener("wheel", wheelZoom);
      document.removeEventListener("keydown", onEscape);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desktop]);

  return (
    <Portal.Root>
      <aside role="dialog" ref={containerRef} className={styles.zoomContainer}>
        <Swiper
          spaceBetween={0}
          slidesPerView={1}
          scrollbar={{ draggable: true }}
          navigation
          pagination
          zoom={true}
          onSlideChange={(swiper) => {
            setCurrent(swiper.activeIndex);
          }}
          className={clsx(mediaGalleryStyles.swiper, "swiperZoom")}
          modules={[Controller]}
          onSwiper={setSecondSwiper}
          controller={{ control: firstSwiper }}
        >
          {media?.map((image, i: number) => (
            <SwiperSlide
              className={clsx(mediaGalleryStyles.slide, "swiper-no-swiping")}
              key={i}
            >
              {image.type === "image" ? (
                <TransformWrapper initialScale={1} centerOnInit>
                  <TransformComponent
                    wrapperClass={styles.viewport}
                    contentClass={styles.content}
                  >
                    <Image
                      src={image.href}
                      alt=""
                      fill
                      className={mediaGalleryStyles["image-swiper"]}
                      sizes={"3840px"}
                      loading={"eager"}
                      quality={90}
                    />
                  </TransformComponent>
                </TransformWrapper>
              ) : (
                <div className={mediaGalleryStyles.video}>
                  <Suspense fallback={<Skeleton height="100%" />}>
                    <Video
                      url={currentMediaHref}
                      playing
                      loop
                      muted
                      width="100%"
                      height="100%"
                    />
                  </Suspense>
                </div>
              )}
            </SwiperSlide>
          ))}
          <div className={clsx("swiperCount", mediaGalleryStyles.count)}>
            {current + 1} / {media?.length}
          </div>
        </Swiper>
        <button
          title="close"
          className={clsx(styles.zoomClose, styles.zoomButton)}
          onClick={() => setOpen(false)}
        >
          <Close />
        </button>
      </aside>
    </Portal.Root>
  );
};

export default ZoomDialog;
